import { isDefined } from "@omni/utils/helper.utils";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import * as PreferredStoreModule from "@/redux/modules/api/data/preferredStore/preferredStore.module";
import * as PreferredStoreSelectors from "@/redux/modules/api/data/preferredStore/preferredStore.selectors";
import { GetStoreStrategy } from "@/services/store.service";

import { useStoreCode } from "./useStoreCode.hook";

type UseStoreProps = {
    strategy: GetStoreStrategy;
    sku?: string;
};

export const useStore = ({ strategy, sku }: UseStoreProps) => {
    const dispatch = useThunkDispatch();

    const storeCode = useStoreCode({
        strategy: strategy
    });

    const store = useSelector(PreferredStoreSelectors.getStore(strategy, sku));
    const storeLoading = useSelector(PreferredStoreSelectors.isLoading);

    const cartLoading = useSelector(CartSelectors.isLoading);
    const customerLoading = useSelector(CustomerSelectors.isLoading);

    useEffect(() => {
        dispatch(CustomerModule.load());

        if (
            strategy === GetStoreStrategy.Session ||
            strategy === GetStoreStrategy.Checkout
        ) {
            dispatch(CartModule.load());
        }
    }, [strategy, dispatch]);

    useEffect(() => {
        if (cartLoading || customerLoading || !storeCode) return;

        const force = isDefined(store?.code) && store.code !== storeCode;

        dispatch(
            PreferredStoreModule.getPreferredStoreByCode(
                {
                    code: storeCode,
                    strategy: strategy,
                    sku: sku
                },
                force
            )
        );
    }, [
        storeCode,
        store,
        cartLoading,
        customerLoading,
        strategy,
        sku,
        dispatch
    ]);

    return {
        store: store ?? null,
        storeCode: storeCode,
        storeLoading: storeLoading
    };
};
